import { useState, useEffect } from 'react';
import { Map, Marker } from 'pigeon-maps';

// Parse company coordinates (stored as lat,lng)
function parseCompanyCoordinates(value) {
  if (!value) return null;
  const str = String(value).replace(/[\[\]\(\)]/g, '').trim();
  const parts = str.split(',');
  if (parts.length !== 2) return null;
  const lat = parseFloat(parts[0].trim());
  const lng = parseFloat(parts[1].trim());
  if (isNaN(lat) || isNaN(lng)) return null;
  return [lat, lng];
}

// Parse client coordinates (stored as lng,lat - needs flipping)
function parseClientCoordinates(value) {
  if (!value) return null;
  const str = String(value).replace(/[\[\]\(\)]/g, '').trim();
  const parts = str.split(',');
  if (parts.length !== 2) return null;
  const lng = parseFloat(parts[0].trim());
  const lat = parseFloat(parts[1].trim());
  if (isNaN(lat) || isNaN(lng)) return null;
  return [lat, lng]; // Flip to [lat, lng]
}

// Calculate distance between two points in meters
function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export default function ProjectMapModal({ isOpen, onClose, companyGeoTag, clientGeoTag, projectName, clientName }) {
  const [mapCenter, setMapCenter] = useState([51.5074, -0.1278]); // Default to London
  const [zoom, setZoom] = useState(10);
  const [selectedMarker, setSelectedMarker] = useState(null);

  console.log('ProjectMapModal Props:', { isOpen, companyGeoTag, clientGeoTag, projectName, clientName });

  useEffect(() => {
    if (isOpen && (companyGeoTag || clientGeoTag)) {
      const companyCoords = parseCompanyCoordinates(companyGeoTag);
      const clientCoords = parseClientCoordinates(clientGeoTag);
      
      console.log('Parsed coords:', { companyCoords, clientCoords });
      
      if (companyCoords && clientCoords) {
        // Calculate center between company and client
        const centerLat = (companyCoords[0] + clientCoords[0]) / 2;
        const centerLng = (companyCoords[1] + clientCoords[1]) / 2;
        setMapCenter([centerLat, centerLng]);
        
        // Calculate distance to determine appropriate zoom
        const latDiff = Math.abs(companyCoords[0] - clientCoords[0]);
        const lngDiff = Math.abs(companyCoords[1] - clientCoords[1]);
        const maxDiff = Math.max(latDiff, lngDiff);
        
        // Adjust zoom based on distance
        if (maxDiff < 0.01) setZoom(14);
        else if (maxDiff < 0.05) setZoom(12);
        else if (maxDiff < 0.1) setZoom(11);
        else if (maxDiff < 0.5) setZoom(9);
        else setZoom(8);
      } else if (companyCoords) {
        setMapCenter(companyCoords);
        setZoom(13);
      } else if (clientCoords) {
        setMapCenter(clientCoords);
        setZoom(13);
      }
    }
  }, [isOpen, companyGeoTag, clientGeoTag]);

  if (!isOpen) return null;

  const companyCoords = parseCompanyCoordinates(companyGeoTag);
  const clientCoords = parseClientCoordinates(clientGeoTag);

  // Calculate distance if both coordinates exist
  let distance = null;
  if (companyCoords && clientCoords) {
    const distanceMeters = getDistance(companyCoords[0], companyCoords[1], clientCoords[0], clientCoords[1]);
    distance = (distanceMeters / 1609.34).toFixed(2); // Convert to miles
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="relative w-full h-full max-w-6xl max-h-screen bg-white rounded-lg overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-indigo-600 to-purple-800 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">{projectName || 'Project'} - Route Map</h2>
            <p className="text-indigo-100 text-sm">Company to Client Location</p>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition-colors"
            title="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="relative h-[calc(100%-5rem)] w-full">
          <Map center={mapCenter} zoom={zoom} height="100%" width="100%">
            {companyCoords && (
              <Marker
                anchor={companyCoords}
                color="#000000"
                width={40}
                height={40}
                onClick={() => setSelectedMarker({ type: 'company' })}
              />
            )}
            {clientCoords && (
              <Marker
                anchor={clientCoords}
                color="#10b981"
                width={40}
                height={40}
                onClick={() => setSelectedMarker({ type: 'client' })}
              />
            )}
          </Map>

          {/* Marker Info Popup */}
          {selectedMarker && (
            <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-4 border border-gray-200 z-10 max-w-xs">
              <button
                onClick={() => setSelectedMarker(null)}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="space-y-2">
                {selectedMarker.type === 'company' ? (
                  <>
                    <h3 className="font-semibold text-gray-900">Home</h3>
                    <p className="text-sm text-gray-600">Company Location</p>
                  </>
                ) : (
                  <>
                    <h3 className="font-semibold text-gray-900">Client Address</h3>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-900">{clientName || 'Client'}</span>
                    </p>
                    {distance && (
                      <p className="text-sm text-gray-600">
                        Distance: <span className="font-medium text-gray-900">{distance} miles</span>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full inline-block bg-black"></span>
              <span className="text-gray-600">Company Location (Home)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full inline-block bg-emerald-500"></span>
              <span className="text-gray-600">Client Address - {clientName || 'Client'}</span>
            </div>
            {distance && (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span className="text-gray-600">Distance: {distance} miles</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
